import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Stepper, StepperModule } from 'primeng/stepper';
import { ButtonModule } from 'primeng/button';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AccordionModule } from 'primeng/accordion';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CampaignService } from '../../services/campaign.service';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardModule } from 'primeng/card';
import { GenericButtonComponent } from "../../../global/component/generic/generic-button/generic-button.component";
import { ToggleButtonModule } from 'primeng/togglebutton';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { AddUpdateVendorService } from '../../services/add-update-vendor.service';
import { SelectListItem } from '../../models/SelectListItem';
import { LookupItem } from '../../../global/model/SearchModel';
import { AutocompleteService } from '../../services/autocomplete.service';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs';
import { EntityType } from '../../models/ConstantsAndEnums';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ExpenseApprovalViewModel } from '../../models/ExpenseApprovalViewModel';
import { TableModule } from 'primeng/table';
import { VendorIdToApproverViewModel, VendorW9ViewModel } from '../../models/VendorViewModel';
import { InputMaskModule } from 'primeng/inputmask';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'vendor-add-update',
  templateUrl: './vendor-add-update.component.html',
  styleUrls: ['./vendor-add-update.component.scss'],
  standalone: true,
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, AccordionModule, InputTextModule, MultiSelectModule, StepperModule, ButtonModule, InputSwitchModule,
    ToastModule, ConfirmDialogModule, CheckboxModule, RadioButtonModule, DropdownModule, CardModule, ProgressSpinnerModule,
    ToggleButtonModule, FileUploadModule, AutoCompleteModule, GenericButtonComponent, TableModule, InputMaskModule, DialogModule],
  providers: [
    ConfirmationService,
    MessageService,
    CampaignService,
    AddUpdateVendorService,
    AutocompleteService
  ]
})
export class VendorAddUpdateComponent implements OnInit {
  @ViewChild('addVendorStepper') addVendorStepper!: Stepper;
  @Input() mode: 'add' | 'update' = 'add';
  @Input() vendorId!: string;
  @Input() campaignId!: number;
  @Input() childCampaignId!: number;
  @Output() vendorAddedUpdated = new EventEmitter<{ label: string; value: string }>();
  @Output() closeDialog = new EventEmitter<void>();
  vendorForm!: FormGroup;
  vendorTypes: LookupItem[] = [];
  states: SelectListItem[] = [];
  vendor1099Types: SelectListItem[] = [];
  complianceTypes: SelectListItem[] = [];
  paymentTypes: LookupItem[] = [];
  accountSuggestions: any = [];
  onIcon: string = 'pi pi-check-circle';
  approverSuggestions: any = [];
  internalExpense = true;
  approvers: VendorIdToApproverViewModel[] = [];
  attachments: VendorW9ViewModel[] = [];
  dialogVisible = false;
  dialogMessage = "";
  dialogAcceptCallback: (() => void) | null = null;
  dialogRejectCallback: (() => void) | null = null;
  @ViewChild('fileUploader') fileUploader!: FileUpload;

  constructor(private fb: FormBuilder, private vendorService: AddUpdateVendorService, private messageService: MessageService, private autoCompleteService: AutocompleteService) {
    this.vendorForm = this.fb.group({
      vendorInfo_CampaignId: ['', Validators.required],
      vendorInfo_VendorId: [''],
      vendorTypeId: ['', Validators.required],
      firstName: [''],
      lastName: [''],
      organizationName: [''],
      vendorInfo_TaxId: [''],
      vendorInfo_1099Id: ['', Validators.required],
      w9Forms: [[]],
      vendorInfo_DefaultPaymentMethodId: [''],
      vendorInfo_DefaultFECLineNumber: [''],
      vendorInfo_DefaultFECDescription: [''],
      glnewAccount: [''],
      vendorInfo_DefaultGLAccount: [''],
      vendorInfo_ContactName: [''],
      vendorInfo_Phone: [''],
      vendorInfo_Email: ['', Validators.email],
      vendorInfo_AddressLine1: ['', Validators.required],
      vendorInfo_AddressLine2: [''],
      vendorInfo_City: ['', Validators.required],
      vendorInfo_State: ['', Validators.required],
      vendorInfo_Zip: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      vendorInfo_Default_NoApprovalRequired: [true],
      approver: [''],
      defaultApproversList: [[]],
      vendorInfo_VendorCheckName: ['', Validators.required],
      vendorInfo_RemitToAddressLine1: [''],
      vendorInfo_RemitToAddressLine2: [''],
      vendorInfo_RemitToCity: [''],
      vendorInfo_RemitToState: [''],
      vendorInfo_RemitToZip: ['', Validators.pattern(/^\d{5}(-\d{4})?$/)],
    });
    this.vendorForm.get('vendorTypeId')?.valueChanges.subscribe(() => {
      this.updateFormValidators();
      this.updateDisplayName();
    });

    this.vendorForm.get('firstName')?.valueChanges.subscribe(() => {
      this.updateDisplayName();
    });

    this.vendorForm.get('lastName')?.valueChanges.subscribe(() => {
      this.updateDisplayName();
    });

    this.vendorForm.get('organizationName')?.valueChanges.subscribe(() => {
      this.updateDisplayName();
    });
  }

  updateDisplayName() {
    const firstName = this.vendorForm.get('firstName')?.value || '';
    const lastName = this.vendorForm.get('lastName')?.value || '';
    const organization = this.vendorForm.get('organizationName')?.value || '';
    if (this.isIndividualType) {
      this.vendorForm.get('vendorInfo_VendorCheckName')?.setValue(`${firstName}${lastName}`.trim());
    } else if (this.isOrganizationType) {
      this.vendorForm.get('vendorInfo_VendorCheckName')?.setValue(organization);
    } else {
      this.vendorForm.get('vendorInfo_VendorCheckName')?.setValue('');
    }
  }
  updateFormValidators(): void {
    this.vendorForm.get('firstName')?.clearValidators();
    this.vendorForm.get('lastName')?.clearValidators();
    this.vendorForm.get('organizationName')?.clearValidators();
    if (this.isIndividualType) {
      this.vendorForm.get('firstName')?.setValidators([Validators.required]);
      this.vendorForm.get('lastName')?.setValidators([Validators.required]);
    } else if (this.isOrganizationType) {
      this.vendorForm.get('organizationName')?.setValidators([Validators.required]);
    }
    this.vendorForm.get('firstName')?.updateValueAndValidity();
    this.vendorForm.get('lastName')?.updateValueAndValidity();
    this.vendorForm.get('organizationName')?.updateValueAndValidity();
  }
  ngOnInit(): void {
    this.vendorForm.get('vendorInfo_CampaignId')?.setValue(this.campaignId);
    this.vendorService.getVendorLookups(this.campaignId).subscribe({
      next: (data) => {
        this.vendorTypes = data.vendorTypes
        this.states = data.states
        this.vendor1099Types = data.vendor1099Types
        this.complianceTypes = data.complianceList
        this.paymentTypes = data.paymentMethods
        if (this.mode == "update") {
          this.vendorService.getVendor(this.vendorId, this.campaignId).subscribe({
            next: (newdata) => {
              this.internalExpense = newdata.vendorInfo_Default_NoApprovalRequired;
              this.vendorForm = this.fb.group({
                vendorInfo_CampaignId: [newdata.vendorInfo_CampaignId, Validators.required],
                vendorInfo_VendorId: [newdata.vendorInfo_VendorId],
                vendorTypeId: [newdata.vendorTypeId, Validators.required],
                firstName: [newdata.firstName],
                lastName: [newdata.lastName],
                organizationName: [newdata.organizationName],
                vendorInfo_TaxId: [newdata.vendorInfo_TaxId],
                vendorInfo_1099Id: [newdata.vendorInfo_1099Id?.toString(), Validators.required],
                w9Forms: [[]],
                vendorInfo_DefaultPaymentMethodId: [newdata.vendorInfo_DefaultPaymentMethodId],
                vendorInfo_DefaultFECLineNumber: [newdata.vendorInfo_DefaultFECLineNumber?.toString()],
                vendorInfo_DefaultFECDescription: [newdata.vendorInfo_DefaultFECDescription],
                glnewAccount: [newdata.glAccountDisplayText],
                vendorInfo_DefaultGLAccount: [newdata.vendorInfo_DefaultGLAccount?.toString()],
                vendorInfo_ContactName: [newdata.vendorInfo_ContactName],
                vendorInfo_Phone: [newdata.vendorInfo_Phone],
                vendorInfo_Email: [newdata.vendorInfo_Email, Validators.email],
                vendorInfo_AddressLine1: [newdata.vendorInfo_AddressLine1, Validators.required],
                vendorInfo_AddressLine2: [newdata.vendorInfo_AddressLine2],
                vendorInfo_City: [newdata.vendorInfo_City, Validators.required],
                vendorInfo_State: [newdata.vendorInfo_State, Validators.required],
                vendorInfo_Zip: [newdata.vendorInfo_Zip, [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
                vendorInfo_Default_NoApprovalRequired: [newdata.vendorInfo_Default_NoApprovalRequired],
                approver: [''],
                defaultApproversList: [newdata.defaultApproversList ?? []],
                vendorInfo_VendorCheckName: [newdata.vendorInfo_VendorCheckName, Validators.required],
                vendorInfo_RemitToAddressLine1: [newdata.vendorInfo_RemitToAddressLine1],
                vendorInfo_RemitToAddressLine2: [newdata.vendorInfo_RemitToAddressLine2],
                vendorInfo_RemitToCity: [newdata.vendorInfo_RemitToCity],
                vendorInfo_RemitToState: [newdata.vendorInfo_RemitToState],
                vendorInfo_RemitToZip: [newdata.vendorInfo_RemitToZip, Validators.pattern(/^\d{5}(-\d{4})?$/)],
              });
              this.approvers = newdata.defaultApproversList ?? [];
              if (this.approvers.length > 0)
                this.vendorForm.get('vendorInfo_Default_NoApprovalRequired')?.disable();
              this.vendorService.getVendorW9s(this.vendorId).subscribe({
                next: (attachmentData) => {
                  this.attachments = attachmentData
                },
                error: () => {
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to Get Attachments!'
                  });
                }
              })
            },
            error: () => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to Get Vendor!'
              });
            }
          });
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to Get Vendor Lookups!'
        });
      }
    });
  }

  toggleInternalExpense(event: any) {
    this.internalExpense = event.checked;
  }

  get vendorType(): string {
    var selectedKey = this.vendorForm.get('vendorTypeId')?.value;
    const selectedObject = this.vendorTypes.find(type => type.key === selectedKey);
    if (selectedObject) {
      return selectedObject.value;
    }
    else {
      return "";
    }
  }

  get isIndividualType(): boolean {
    return ['IND', 'CAN'].includes(this.vendorType);
  }

  get isOrganizationType(): boolean {
    return ['ORG', 'CCM', 'COM', 'PAC', 'PTY'].includes(this.vendorType);
  }

  onFileChange(event: any): void {
    const files: File[] = event.files;
    console.debug('Selected files:', files);

    if (files && files.length > 0) {
      const fileArray: File[] = Array.from(files);
      this.vendorForm.get('w9Forms')?.setValue(fileArray);
    }
  }

  searchAccount(query: string): void {
    if (!query) {
      this.accountSuggestions = [];
      return;
    }
    this.autoCompleteService.getAutoCompleteResults(query, EntityType.GLAccounts, this.campaignId).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      catchError(() => [])
    ).subscribe((results) => {
      this.accountSuggestions = this.mapToLabelValue(results);
      this.vendorForm.get('vendorInfo_DefaultGLAccount')?.updateValueAndValidity();
    });
  }

  deleteAttachment(attachment: VendorW9ViewModel) {
    this.dialogVisible = true;
    this.confirmAction(
      `Are you sure you want to delete ${attachment.fileName}?`,
      () => {
        this.vendorService.deletew9s(attachment.attachmentId).subscribe({
          next: () => {
            this.vendorService.getVendorW9s(this.vendorId).subscribe({
              next: (attachmentData) => {
                this.attachments = attachmentData;
              },
              error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to Get Attachments!' });
              }
            });
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to Delete Attachment!' });
          }
        });
      },
      () => {
        this.dialogVisible = false;
      }
    );
  }

  confirmAction(
    message: string,
    acceptCallback: () => void,
    rejectCallback?: () => void
  ) {
    this.dialogMessage = message;
    this.dialogAcceptCallback = acceptCallback;
    this.dialogRejectCallback = rejectCallback || null;
    this.dialogVisible = true;
  }

  onDialogAccept() {
    this.dialogVisible = false;
    this.dialogAcceptCallback?.();
  }

  onDialogReject() {
    this.dialogVisible = false;
    this.dialogRejectCallback?.();
  }

  onAccountSelect(account: any) {
    const glAccountId = account.value.value;
    this.vendorForm.get('vendorInfo_DefaultGLAccount')?.setValue(glAccountId);
  }

  searchApprover(query: string): void {
    if (!query) {
      this.approverSuggestions = [];
      return;
    }
    this.autoCompleteService.getUserResults(query, this.campaignId, 0).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      catchError(() => [])
    ).subscribe((results) => {
      console.debug(results);
      this.approverSuggestions = this.mapToLabelValue2(results);
    });
  }

  onApproverSelect(approver: any) {
    const approverId = approver.value.value;
    const approvername = approver.value.label;
    const existing = this.approvers.find(
      a => a.userId === approverId
    );
    if (!existing) {
      this.approvers.push({
        userId: approverId,
        name: approvername,
        vendorIdtoApproverId: 0,
        vendorId: '',
        campaignId: 0,
        createdBy: '00000000-0000-0000-0000-000000000000',
        createdTimeStamp: new Date(),
        lastEditBy: '00000000-0000-0000-0000-000000000000',
        lastEditTimeStamp: new Date(),
        workflowStateId: 0
      });
      this.vendorForm.get('defaultApproversList')?.setValue(this.approvers);
    }
    this.vendorForm.get('approver')?.setValue('');
    if (this.approvers.length === 0)
      this.vendorForm.get('vendorInfo_Default_NoApprovalRequired')?.enable();
    else
      this.vendorForm.get('vendorInfo_Default_NoApprovalRequired')?.disable();
  }

  deleteApprover(approver: any) {
    this.dialogVisible = true;
    this.confirmAction(
      `Are you sure you want to delete ${approver.name}?`,
      () => {
        const expenseApprovalId = approver.userId;
        this.approvers = this.approvers.filter(
          a => a.userId !== expenseApprovalId
        );
        this.vendorForm.get('defaultApproversList')?.setValue(this.approvers);
        if (this.approvers.length === 0)
          this.vendorForm.get('vendorInfo_Default_NoApprovalRequired')?.enable();
        else
          this.vendorForm.get('vendorInfo_Default_NoApprovalRequired')?.disable();
      },
      () => {
        this.dialogVisible = false;
      }
    );
   
  }

  private mapToLabelValue(items: any[]): { label: string, value: string }[] {
    return items?.map((item: any) => ({ label: item.text, value: item.value })) || [];
  }

  private mapToLabelValue2(items: any[]): { label: string, value: string }[] {
    return items?.map((item: any) => ({ label: item.name, value: item.id })) || [];
  }

  getInvalidControls(formGroup: FormGroup): string[] {
    const invalidControls: string[] = [];
    const controls = formGroup.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalidControls.push(name);
      }
    }
    return invalidControls;
  }

  validateSpecificControls(formGroup: FormGroup, controlNames: string[]): void {
    controlNames.forEach((controlName) => {
      const control = formGroup.get(controlName);
      if (control) {
        control.markAsTouched(); // Ensures validation messages are displayed
        control.updateValueAndValidity(); // Triggers validation for this control
      }
    });
  }

  hasCommonElement(array1: string[], array2: string[]): boolean {
    return array1.some(element => array2.includes(element));
  }

  nextStep1() {
    console.debug(this.vendorForm.value);
    var step1validationControls = []
    if (this.isIndividualType)
      step1validationControls = ['vendorTypeId', 'vendorInfo_1099Id', 'firstName', 'lastName'];
    else
      step1validationControls = ['vendorTypeId', 'vendorInfo_1099Id', 'organizationName'];
    this.validateSpecificControls(this.vendorForm, step1validationControls);
    if (this.hasCommonElement(this.getInvalidControls(this.vendorForm), step1validationControls)) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fix validation errors before continuing to next step!' });
    } else {
      this.addVendorStepper.activeStep = 1;
    }
  }
  nextStep2() {
    console.debug(this.vendorForm.value);
    var step2validationControls = ['vendorInfo_AddressLine1', 'vendorInfo_City', 'vendorInfo_State', 'vendorInfo_Zip'];
    this.validateSpecificControls(this.vendorForm, step2validationControls);
    if (this.hasCommonElement(this.getInvalidControls(this.vendorForm), step2validationControls)) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fix validation errors before continuing to next step!' });
    } else {
      this.addVendorStepper.activeStep = 2;
    }
  }
  nextStep3() {
    this.vendorForm.get('defaultApproversList')?.setValue(this.approvers);
    this.addVendorStepper.activeStep = 3;
  }
  prevStep1() {
    this.addVendorStepper.activeStep = 0;
  }
  prevStep2() {
    this.addVendorStepper.activeStep = 1;
  }
  prevStep3() {
    this.addVendorStepper.activeStep = 2;
  }
  onFileUpload(event: any) {
    const files: File[] = event.files;
    if (!files || files.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select files to upload.'
      });
      return;
    }
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file, file.name);
      formData.append('VendorId', this.vendorId.toString());
      formData.append('CampaignId', this.campaignId.toString());
    });
    this.SaveAttachments(formData);
    this.fileUploader.clear();
  }
  SaveAttachments(formData: FormData) {
    this.vendorService.addw9s(formData).subscribe({
      next: () => {
        this.vendorService.getVendorW9s(this.vendorId).subscribe({
          next: (attachmentData) => {
            this.attachments = attachmentData;
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to Get Attachments!' });
          }
        });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to Save Attachments!' });
      }
    });
  }

  onDownloadW9(attachment: VendorW9ViewModel, event: MouseEvent) {
    event.preventDefault();
    this.vendorService.downloadW9(attachment.fileName).subscribe({
      next: (blob) => {
        const a = document.createElement('a');
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.download = attachment.fileName;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Download File Failed!' });
      },
    });
  }

  vendorSubmit() {
    if (this.vendorForm.valid) {
      console.debug(this.vendorForm.value);
      this.vendorService.saveVendor(this.vendorForm.value).subscribe({
        next: (newvendorId: string) => {
          const files: File[] = this.vendorForm.get('w9Forms')?.value
          if (files.length > 0 && this.mode == "add") {
            const formData = new FormData();
            files.forEach(file => {
              formData.append('files', file, file.name);
              formData.append('VendorId', newvendorId);
              formData.append('CampaignId', this.campaignId.toString());
            });
            this.SaveAttachments(formData);
          }
          const newVendor = { label: this.isIndividualType ? this.vendorForm.get('firstName')?.value + " " + this.vendorForm.get('lastName')?.value : this.vendorForm.get('organizationName')?.value, value: newvendorId };
          this.vendorAddedUpdated.emit(newVendor);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save Vendor!'
          });
        }
      });
    } else {
      this.markAllAsTouched(this.vendorForm);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to save Vendor. Please fix the validation errors!' });
    }
  }
  markAllAsTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      formGroup.get(field)?.markAsTouched();
    });
  }

  closeVendor(){
    this.closeDialog.emit();
  }
}
